// 5-Stage High-Converting Product Onboarding (2026)
//
// Structured across the 5 core stacks:
// 1. Analytics stack (GA4, GTM, PostHog, Clarity)
// 2. Social stack (Meta Ads, LinkedIn Ads, Google Ads, Meta Social, X)
// 3. SEO & AEO (Google Search Console, Bing, Opinly, Ahrefs, SEMrush)
// 4. Event tracking (1-Click GTM deploy, fk.js snippet, API)
// 5. Bottom of Funnel & Monetisation (Stripe, MySQL, Postgres, MongoDB)
//
// At each stage, surfaces available integrations, explains how they collaborate,
// highlights detected website tools, and lets users connect inline or continue.
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError, type Connection } from "../api";
import { go } from "../router";
import { pageview, track } from "../lib/analytics";
import { useAsync } from "../useAsync";
import {
  ClarityConnectForm, GoogleConnectButton, GrantAccessInstead,
  MongoCredentialsForm, MysqlCredentialsForm, PosthogConnectForm,
  PostgresCredentialsForm, MetaAdsConnectForm, LinkedinAdsConnectForm,
  MetaSocialConnectForm, XSocialConnectForm, StripeCredentialsForm,
  BingConnectForm, OpinlyConnectForm, AhrefsConnectForm, SemrushConnectForm,
  TrackingInstall, WebsiteDetector, finishOnboarding, startOnboarding,
} from "../components/connect/forms";
import { IntegrationMark } from "../components/IntegrationMark";
import { adsOffered, gscOauthOffered } from "../lib/flags";

const GOOGLE_KINDS = adsOffered ? ["ga4", "gtm", "google_ads"] : ["ga4", "gtm"];

export type Step =
  | "choose"
  | "claim"
  | "manual"
  | "handoff"
  | "analytics"
  | "social"
  | "seo"
  | "tracking"
  | "monetisation"
  | "done";

const SLUG_KEY = "fk_onboarding_slug";
const DOMAIN_KEY = "fk_onboarding_domain";
const STEP_KEY = "fk_onboarding_step";

function savedSlug(): string {
  return localStorage.getItem(SLUG_KEY) ?? "";
}

function savedDomain(): string {
  return localStorage.getItem(DOMAIN_KEY) ?? "";
}

function savedStep(): Step | null {
  const s = localStorage.getItem(STEP_KEY) as Step | null;
  if (s && ["analytics", "social", "seo", "tracking", "monetisation", "done"].includes(s)) {
    return s;
  }
  return null;
}

const STACK_STEPS: Array<{ id: Step; label: string; num: number; icon: string }> = [
  { id: "analytics", label: "Analytics", num: 1, icon: "ga4" },
  { id: "social", label: "Social & Ads", num: 2, icon: "meta_ads" },
  { id: "seo", label: "SEO & AEO", num: 3, icon: "opinly" },
  { id: "tracking", label: "Tracking", num: 4, icon: "tracking" },
  { id: "monetisation", label: "Monetisation", num: 5, icon: "stripe" },
];

export function Onboarding({ onCreated, startAt }: { onCreated: () => void; startAt?: Step }) {
  const [step, setStepState] = useState<Step>(() => {
    if (savedSlug()) {
      return savedStep() ?? "analytics";
    }
    // First-run: skip the choose screen. Empty-workspace signups were bouncing
    // before creating a product; create → connect is the path that activates.
    return startAt ?? "manual";
  });
  const [slug, setSlug] = useState(savedSlug);
  const [domain, setDomain] = useState(savedDomain);
  const [detectedMap, setDetectedMap] = useState<Map<string, string>>(new Map());
  const added = startAt === "manual";

  const conns = useAsync(() => (slug ? api.connections() : Promise.resolve([])), [slug]);
  const activeRows = (conns.data ?? []).filter((c) => c.product_slug === slug);
  const isConnected = (kind: string) => activeRows.some((c) => c.kind === kind && c.status === "active");

  const setStep = (next: Step) => {
    setStepState(next);
    if (["analytics", "social", "seo", "tracking", "monetisation", "done"].includes(next)) {
      localStorage.setItem(STEP_KEY, next);
    }
  };

  useEffect(() => {
    track("onboarding_start");
  }, []);

  useEffect(() => {
    const titles: Record<Step, string> = {
      choose: "Onboarding",
      claim: "Onboarding · claim",
      manual: "Onboarding · project",
      handoff: "Onboarding · handoff",
      analytics: "Onboarding · 1. Analytics stack",
      social: "Onboarding · 2. Social stack",
      seo: "Onboarding · 3. SEO & AEO",
      tracking: "Onboarding · 4. Event tracking",
      monetisation: "Onboarding · 5. Monetisation",
      done: "Onboarding · ready",
    };
    pageview(`/#/onboarding/${step === "choose" ? "" : step}`.replace(/\/$/, ""), titles[step]);
    track("onboarding_step", { step });
  }, [step]);

  const pick = (next: Exclude<Step, "choose" | "analytics" | "social" | "seo" | "tracking" | "monetisation" | "done">) => {
    track("onboarding_path", { path: next });
    setStep(next);
  };

  const afterProduct = (createdSlug: string, createdDomain?: string) => {
    localStorage.setItem(SLUG_KEY, createdSlug);
    if (createdDomain) {
      localStorage.setItem(DOMAIN_KEY, createdDomain);
      setDomain(createdDomain);
    } else {
      localStorage.removeItem(DOMAIN_KEY);
      setDomain("");
    }
    startOnboarding();
    setSlug(createdSlug);
    setStep("analytics");
  };

  const complete = () => {
    track("onboarding_complete", { slug });
    // Server-side signal so the operator notification can fire — the wizard is
    // otherwise entirely client-side. Fire-and-forget: finishing must not depend
    // on it, and the endpoint is idempotent if this screen is revisited.
    if (slug) void api.completeOnboarding(slug).catch(() => {});
    finishOnboarding();
    localStorage.removeItem(SLUG_KEY);
    localStorage.removeItem(DOMAIN_KEY);
    localStorage.removeItem(STEP_KEY);
    onCreated();
    go({ name: "product", slug: slug || null, section: "dashboard" });
  };

  const back = () => {
    if (step === "analytics") setStep("manual");
    else if (step === "social") setStep("analytics");
    else if (step === "seo") setStep("social");
    else if (step === "tracking") setStep("seo");
    else if (step === "monetisation") setStep("tracking");
    else if (step === "done") setStep("monetisation");
    else if (added) go({ name: "product", slug: null, section: "dashboard" });
    else setStep("choose");
  };

  const isStackStep = ["analytics", "social", "seo", "tracking", "monetisation", "done"].includes(step);

  return (
    <div className="page" style={{ paddingTop: 36, maxWidth: 760 }}>
      {step !== "choose" && (
        <button type="button" className="btn-text" onClick={back} style={{ marginBottom: 12, paddingLeft: 0 }}>
          ← Back
        </button>
      )}

      {isStackStep && slug && (
        <StepBar current={step} onSelectStep={(s) => setStep(s)} isConnected={isConnected} />
      )}

      {step === "choose" && <Choose onPick={pick} />}
      {step === "claim" && <Claim />}
      {step === "manual" && (
        <ManualSetup
          onCreated={afterProduct}
          added={added}
          onAlt={added ? undefined : (s) => setStep(s)}
        />
      )}
      {step === "handoff" && <Handoff onReady={onCreated} />}

      {step === "analytics" && slug && (
        <AnalyticsStep
          slug={slug}
          domain={domain}
          detectedMap={detectedMap}
          setDetectedMap={setDetectedMap}
          isConnected={isConnected}
          onReload={() => conns.reload()}
          onNext={() => setStep("social")}
          onSkip={() => setStep("social")}
        />
      )}

      {step === "social" && slug && (
        <SocialStep
          slug={slug}
          detectedMap={detectedMap}
          isConnected={isConnected}
          onReload={() => conns.reload()}
          onNext={() => setStep("seo")}
          onSkip={() => setStep("seo")}
        />
      )}

      {step === "seo" && slug && (
        <SeoStep
          slug={slug}
          domain={domain}
          detectedMap={detectedMap}
          isConnected={isConnected}
          onReload={() => conns.reload()}
          onNext={() => setStep("tracking")}
          onSkip={() => setStep("tracking")}
        />
      )}

      {step === "tracking" && slug && (
        <TrackingStep
          slug={slug}
          domain={domain}
          isConnected={isConnected}
          onNext={() => setStep("monetisation")}
          onSkip={() => setStep("monetisation")}
        />
      )}

      {step === "monetisation" && slug && (
        <MonetisationStep
          slug={slug}
          detectedMap={detectedMap}
          isConnected={isConnected}
          onReload={() => conns.reload()}
          onNext={() => setStep("done")}
          onSkip={() => setStep("done")}
        />
      )}

      {step === "done" && slug && (
        <DoneScorecard
          slug={slug}
          conns={conns.data ?? []}
          added={added}
          onFinish={complete}
        />
      )}
    </div>
  );
}

function StepBar({
  current,
  onSelectStep,
  isConnected,
}: {
  current: Step;
  onSelectStep: (s: Step) => void;
  isConnected: (kind: string) => boolean;
}) {
  const stackDone = (id: Step): boolean => {
    if (id === "analytics") return isConnected("ga4") || isConnected("gtm") || isConnected("posthog") || isConnected("clarity");
    if (id === "social") return isConnected("meta_ads") || isConnected("linkedin_ads") || isConnected("google_ads") || isConnected("meta_social") || isConnected("x_social");
    if (id === "seo") return isConnected("gsc") || isConnected("bing") || isConnected("opinly") || isConnected("ahrefs") || isConnected("semrush");
    if (id === "tracking") return isConnected("gtm");
    if (id === "monetisation") return isConnected("stripe") || isConnected("mysql") || isConnected("postgres") || isConnected("mongo");
    return false;
  };

  return (
    <nav className="onboarding-nav" aria-label="Onboarding Progress">
      {STACK_STEPS.map((s) => {
        const isActive = current === s.id;
        const isDone = stackDone(s.id);
        return (
          <button
            key={s.id}
            type="button"
            className={`onboarding-step-pill ${isActive ? "active" : ""} ${isDone ? "completed" : ""}`}
            onClick={() => onSelectStep(s.id)}
          >
            <span className="onboarding-step-badge">
              {isDone ? "✓" : s.num}
            </span>
            <span>{s.label}</span>
          </button>
        );
      })}
      <button
        type="button"
        className={`onboarding-step-pill ${current === "done" ? "active" : ""}`}
        onClick={() => onSelectStep("done")}
      >
        <span className="onboarding-step-badge">★</span>
        <span>Ready</span>
      </button>
    </nav>
  );
}

function Choose({ onPick }: { onPick: (s: "claim" | "manual" | "handoff") => void }) {
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1>How do you want to set this up?</h1>
        <span className="sub" style={{ maxWidth: 540 }}>
          An AI coding agent can create the project and wire sources. You can also walk the step-by-step 5-stack setup here.
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 26 }}>
        <Choice
          title="I'll set it up here"
          sub="Add your project, scan your website, and connect your analytics, social, SEO, and monetisation stack."
          onClick={() => onPick("manual")}
        />
        <Choice
          title="My AI already set this up"
          sub="Paste the claim link it gave you. You'll set a password and take over that account."
          onClick={() => onPick("claim")}
        />
        <Choice
          title="Give this to my AI"
          sub="We'll mint an API key. Hand it to Claude Code, Cursor, Lovable, or the fk CLI."
          onClick={() => onPick("handoff")}
        />
      </div>
    </>
  );
}

function Choice({ title, sub, onClick }: { title: string; sub: string; onClick: () => void }) {
  return (
    <button type="button" className="choice" onClick={onClick}>
      <span className="choice-title">{title}</span>
      <span className="choice-sub">{sub}</span>
    </button>
  );
}

function parseClaimToken(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const marker = "#/invite/";
  const at = s.indexOf(marker);
  if (at >= 0) {
    const rest = s.slice(at + marker.length);
    const token = decodeURIComponent(rest.split(/[?#]/)[0] ?? "");
    return token || null;
  }
  if (/^invite\//.test(s)) {
    const token = decodeURIComponent(s.slice("invite/".length).split(/[?#]/)[0] ?? "");
    return token || null;
  }
  if (/^fki_[A-Za-z0-9_-]+$/.test(s)) return s;
  return null;
}

function Claim() {
  const [raw, setRaw] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const token = parseClaimToken(raw);
    if (!token) {
      setError("That doesn't look like a claim link. It should contain #/invite/ or start with fki_.");
      return;
    }
    go({ name: "invite", token });
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1>Paste the claim link</h1>
        <span className="sub" style={{ maxWidth: 520 }}>
          Your AI printed this when it created the account. Opening it signs you into that account.
        </span>
      </div>
      <form onSubmit={submit} className="panel" style={{ marginTop: 26, display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="field">
          <label htmlFor="claim">Claim link</label>
          <input
            id="claim"
            className="input"
            required
            value={raw}
            onChange={(e) => { setRaw(e.target.value); setError(null); }}
            placeholder="https://app.funnelkeeper.com/#/invite/fki_…"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        {error && <div className="form-error">{error}</div>}
        <div>
          <button type="submit" className="btn-solid" disabled={!raw.trim()}>Continue</button>
        </div>
      </form>
    </>
  );
}

function ManualSetup({
  onCreated,
  added,
  onAlt,
}: {
  onCreated: (slug: string, domain?: string) => void;
  added?: boolean;
  onAlt?: (s: "claim" | "handoff") => void;
}) {
  const [domain, setDomain] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const derivedSlug = (v: string) =>
    v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

  const deriveFromUrl = (raw: string) => {
    const clean = raw.trim().replace(/^https?:\/\//i, "").replace(/\/.*$/, "").replace(/^www\./i, "");
    if (!clean) return { name: "", slug: "" };
    const parts = clean.split(".");
    const base = parts[0] || clean;
    const title = base.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const s = base.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
    return { name: title, slug: s };
  };

  const onDomainChange = (val: string) => {
    setDomain(val);
    const { name: suggestedName, slug: suggestedSlug } = deriveFromUrl(val);
    if (!nameTouched && suggestedName) setName(suggestedName);
    if (!slugTouched && suggestedSlug) setSlug(suggestedSlug);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api.createProduct({
        name,
        slug,
        currency,
        domain: domain.trim() || undefined,
      });
      track("product_created", { currency, slug: created.slug });
      onCreated(created.slug, domain.trim() || undefined);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1>{added ? "Add a project" : "Add your project"}</h1>
        <span className="sub" style={{ maxWidth: 540 }}>
          {added
            ? "A project is one measured business whose spend and revenue the Keeper watches. Enter your website URL to auto-detect your stack."
            : "Enter your project name and website. We'll auto-detect your active tools and guide you through connecting each stack."}
        </span>
      </div>

      <form onSubmit={submit} className="panel" style={{ marginTop: 26, display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="field">
          <label htmlFor="p-domain">Website URL (recommended)</label>
          <input
            id="p-domain"
            className="input"
            value={domain}
            placeholder="https://example.com"
            onChange={(e) => onDomainChange(e.target.value)}
          />
          <span className="form-note">Used to auto-detect Google Analytics, PostHog, Clarity, Meta Pixel, and Stripe.</span>
        </div>
        <div className="field">
          <label htmlFor="p-name">Project name</label>
          <input
            id="p-name"
            className="input"
            required
            value={name}
            onChange={(e) => {
              setNameTouched(true);
              setName(e.target.value);
              if (!slugTouched) setSlug(derivedSlug(e.target.value));
            }}
          />
        </div>
        <div className="field">
          <label htmlFor="p-slug">Slug (used in URLs & API)</label>
          <input
            id="p-slug"
            className="input num"
            required
            pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
            value={slug}
            onChange={(e) => { setSlugTouched(true); setSlug(derivedSlug(e.target.value)); }}
          />
        </div>
        <div className="field">
          <label htmlFor="p-currency">Primary currency</label>
          <select id="p-currency" className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {["USD", "AUD", "EUR", "GBP", "NZD", "CAD"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        {error && <div className="form-error">{error}</div>}
        <div>
          <button type="submit" className="btn-solid" disabled={busy || !name || !slug}>
            {busy ? "Creating project…" : "Continue to connect sources →"}
          </button>
        </div>
      </form>
      {onAlt && (
        <p className="sub" style={{ marginTop: 16, maxWidth: 540 }}>
          Have a claim link?{" "}
          <button type="button" className="btn-text" onClick={() => onAlt("claim")}>Paste it</button>
          {" · "}
          <button type="button" className="btn-text" onClick={() => onAlt("handoff")}>Hand this to an agent</button>
        </p>
      )}
    </>
  );
}

function Handoff({ onReady }: { onReady: () => void }) {
  const [key, setKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const mint = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.auth.createKey("onboarding");
      track("api_key_created", { source: "onboarding" });
      setKey(created.key);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Couldn't copy — select the key and copy it manually.");
    }
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1>Hand this to your AI</h1>
        <span className="sub" style={{ maxWidth: 520 }}>
          The key is shown once. Your AI uses it to create the project and connect sources.
        </span>
      </div>

      <div className="panel" style={{ marginTop: 26, display: "flex", flexDirection: "column", gap: 16 }}>
        {!key ? (
          <>
            {error && <div className="form-error">{error}</div>}
            <div>
              <button type="button" className="btn-solid" disabled={busy} onClick={() => void mint()}>
                {busy ? "Minting…" : "Mint an API key"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="field">
              <label>API key (shown once)</label>
              <div className="secret">{key}</div>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" className="btn-solid" onClick={() => void copy()}>
                {copied ? "Copied" : "Copy key"}
              </button>
              <button type="button" className="btn-ghost" onClick={onReady}>
                Refresh — my AI is done
              </button>
            </div>
            <div className="form-note">
              CLI: <code>fk login --key</code>, then paste the key.
              MCP: set <code>FUNNELKEEPER_API_KEY</code> to the same value.
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// STAGE 1: Analytics Stack
// ---------------------------------------------------------------------------
function AnalyticsStep({
  slug,
  domain,
  detectedMap,
  setDetectedMap,
  isConnected,
  onReload,
  onNext,
  onSkip,
}: {
  slug: string;
  domain: string;
  detectedMap: Map<string, string>;
  setDetectedMap: (m: Map<string, string>) => void;
  isConnected: (k: string) => boolean;
  onReload: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const ga4Ok = isConnected("ga4");
  const gtmOk = isConnected("gtm");
  const posthogOk = isConnected("posthog");
  const clarityOk = isConnected("clarity");
  const anyOk = ga4Ok || gtmOk || posthogOk || clarityOk;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1>1. Analytics stack</h1>
          <span className="eyebrow" style={{ color: anyOk ? "var(--green)" : "var(--text-faint)" }}>
            {anyOk ? "Connected" : "Step 1 of 5"}
          </span>
        </div>
        <span className="sub">
          Connect one source now — that's what makes the trial useful. Until something is wired, the dashboard stays empty.
        </span>
      </div>

      {domain && (
        <div style={{ marginTop: 16 }}>
          <WebsiteDetector
            slug={slug}
            defaultUrl={domain}
            compact
            autoScan
            title="Auto-detect tools on your site"
            subtitle="Scans your domain for Google Analytics, GTM, PostHog, Clarity, pixels, and payment gateways."
            onConnected={onReload}
            onDetectedTools={(tools) => {
              const m = new Map<string, string>();
              for (const t of tools) {
                if (t.detected_id) m.set(t.key, t.detected_id);
                else m.set(t.key, "detected");
              }
              setDetectedMap(m);
            }}
          />
        </div>
      )}

      <div className="onboarding-strategy-card" style={{ marginTop: 18 }}>
        <strong>How your analytics stack collaborates:</strong>
        <div>
          • <strong>GA4</strong> provides authoritative top-of-funnel traffic sessions, UTM channel attribution, and landing page metrics.
        </div>
        <div>
          • <strong>PostHog</strong> maps in-app product events to funnel stages and links replay sessions. When both GA4 and PostHog are connected, GA4 owns top-of-funnel traffic, and PostHog contributes funnel events and replay.
        </div>
        <div>
          • <strong>Clarity</strong> captures rage clicks, dead clicks, and visual heatmaps without sampling limits.
        </div>
        <div>
          • <strong>Google Tag Manager</strong> continuously audits live tags so missing measurement IDs never spoil attribution.
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
        <StackSourceCard
          kind="ga4"
          title={adsOffered ? "Google (GA4, Tag Manager, Ads)" : "Google (GA4, Tag Manager)"}
          category="Traffic, channel attribution & tag health"
          sub={adsOffered ? "One sign-in. Ingests GA4 traffic, audits live GTM tags, and pulls Google Ads spend." : "One sign-in. Ingests GA4 traffic and audits live GTM tags."}
          detected={detectedMap.get("ga4") || detectedMap.get("gtm")}
          ok={ga4Ok || gtmOk}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <GoogleConnectButton slug={slug} kinds={GOOGLE_KINDS} />
            <div style={{ borderTop: "1px solid var(--border-hairline)", paddingTop: 10 }}>
              <GrantAccessInstead slug={slug} kinds={["ga4", "gtm"]} onDone={() => onReload()} />
            </div>
          </div>
        </StackSourceCard>

        <StackSourceCard
          kind="posthog"
          title="PostHog"
          category="Product analytics & session replay"
          sub="Turn HogQL events into funnel steps. Connects session replay links and friction metrics directly to landing pages."
          detected={detectedMap.get("posthog")}
          ok={posthogOk}
        >
          <PosthogConnectForm slug={slug} onDone={onReload} />
        </StackSourceCard>

        <StackSourceCard
          kind="clarity"
          title="Microsoft Clarity"
          category="Session recordings & heatmaps"
          sub="Watch user friction in action: rage clicks, dead clicks, and script errors mapped to your landing pages."
          detected={detectedMap.get("clarity")}
          ok={clarityOk}
        >
          <ClarityConnectForm slug={slug} onDone={onReload} />
        </StackSourceCard>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button type="button" className="btn-solid" onClick={onNext}>
          {anyOk ? "Continue to Social Stack →" : "Continue →"}
        </button>
        <button type="button" className="btn-ghost" onClick={onSkip}>
          Skip this stack
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// STAGE 2: Social Stack
// ---------------------------------------------------------------------------
function SocialStep({
  slug,
  detectedMap,
  isConnected,
  onReload,
  onNext,
  onSkip,
}: {
  slug: string;
  detectedMap: Map<string, string>;
  isConnected: (k: string) => boolean;
  onReload: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const metaAdsOk = isConnected("meta_ads");
  const linkedinAdsOk = isConnected("linkedin_ads");
  const googleAdsOk = isConnected("google_ads");
  const metaSocialOk = isConnected("meta_social");
  const xSocialOk = isConnected("x_social");
  const anyOk = metaAdsOk || linkedinAdsOk || googleAdsOk || metaSocialOk || xSocialOk;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1>2. Social & Ad stack</h1>
          <span className="eyebrow" style={{ color: anyOk ? "var(--green)" : "var(--text-faint)" }}>
            {anyOk ? "Connected" : "Step 2 of 5"}
          </span>
        </div>
        <span className="sub">
          Pulls campaign spend for real ROAS calculations, and tracks organic social reach, followers, and engagement.
        </span>
      </div>

      <div className="onboarding-strategy-card" style={{ marginTop: 18 }}>
        <strong>How your social stack collaborates:</strong>
        <div>
          • <strong>Paid ad spend</strong> from {adsOffered ? "Meta Ads, LinkedIn Ads, and Google Ads" : "Meta Ads and LinkedIn Ads"} links directly with customer revenue to compute honest payback and CAC by campaign and creative.
        </div>
        <div>
          • <strong>Organic social</strong> snapshots Facebook Page, Instagram, and X followers, impressions, and viral posts under Demand → Social.
        </div>
        <div>
          • <strong>HITL safety</strong>: The Keeper proposes ad spend changes as reviewable cards; spend is never modified without your approval.
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
        <StackSourceCard
          kind="meta_ads"
          title="Meta Ads"
          category="Paid campaign acquisition"
          sub="Daily campaign, ad-set and ad spend from Facebook & Instagram. Governed by FunnelKeeper's policy walls."
          detected={detectedMap.get("meta_pixel")}
          ok={metaAdsOk}
        >
          <MetaAdsConnectForm slug={slug} onDone={onReload} />
        </StackSourceCard>

        <StackSourceCard
          kind="linkedin_ads"
          title="LinkedIn Ads"
          category="B2B paid acquisition"
          sub="Campaign spend from LinkedIn Campaign Manager alongside Google and Meta for accurate multi-channel attribution."
          detected={detectedMap.get("linkedin_ads")}
          ok={linkedinAdsOk}
        >
          <LinkedinAdsConnectForm slug={slug} onDone={onReload} />
        </StackSourceCard>

        {adsOffered && (
          <StackSourceCard
            kind="google_ads"
            title="Google Ads"
            category="Paid search, video & display spend"
            sub="Search, YouTube and Performance Max spend joined to conversions for true keyword-level ROAS."
            ok={googleAdsOk}
          >
            <GoogleConnectButton slug={slug} kinds={["google_ads"]} />
          </StackSourceCard>
        )}

        <StackSourceCard
          kind="meta_social"
          title="Meta Social (Facebook & Instagram)"
          category="Organic audience & engagement"
          sub="Facebook Page and Instagram Business followers, reach, and top-performing organic posts."
          ok={metaSocialOk}
        >
          <MetaSocialConnectForm slug={slug} onDone={onReload} />
        </StackSourceCard>

        <StackSourceCard
          kind="x_social"
          title="X (Twitter)"
          category="Organic audience & engagement"
          sub="Track public follower growth, impressions, retweets, and engagement rate per tweet."
          ok={xSocialOk}
        >
          <XSocialConnectForm slug={slug} onDone={onReload} />
        </StackSourceCard>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button type="button" className="btn-solid" onClick={onNext}>
          {anyOk ? "Continue to SEO & AEO →" : "Continue →"}
        </button>
        <button type="button" className="btn-ghost" onClick={onSkip}>
          Skip this stack
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// STAGE 3: SEO & AEO Stack
// ---------------------------------------------------------------------------
function SeoStep({
  slug,
  domain,
  detectedMap,
  isConnected,
  onReload,
  onNext,
  onSkip,
}: {
  slug: string;
  domain: string;
  detectedMap: Map<string, string>;
  isConnected: (k: string) => boolean;
  onReload: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const gscOk = isConnected("gsc");
  const bingOk = isConnected("bing");
  const opinlyOk = isConnected("opinly");
  const ahrefsOk = isConnected("ahrefs");
  const semrushOk = isConnected("semrush");
  const anyOk = gscOk || bingOk || opinlyOk || ahrefsOk || semrushOk;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1>3. SEO & AEO stack</h1>
          <span className="eyebrow" style={{ color: anyOk ? "var(--green)" : "var(--text-faint)" }}>
            {anyOk ? "Connected" : "Step 3 of 5"}
          </span>
        </div>
        <span className="sub">
          Tracks search engine queries and clicks, alongside Generative AI visibility (ChatGPT, Claude, Gemini) and domain authority.
        </span>
      </div>

      <div className="onboarding-strategy-card" style={{ marginTop: 18 }}>
        <strong>How your search & AI visibility stack collaborates:</strong>
        <div>
          • <strong>Search Console & Bing</strong> feed the top of your demand funnel with real search queries, positions, impressions, and CTR (up to 16 months backfilled).
        </div>
        <div>
          • <strong>Opinly (AEO)</strong> monitors your brand's citation frequency and answer share in generative AI engines (ChatGPT, Gemini, Claude) plus keyword ranks and competitor gaps.
        </div>
        <div>
          • <strong>Ahrefs & SEMrush</strong> preserve your API units with guarded weekly snapshots of Domain Rating, referring domains, and organic keyword positions.
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
        <StackSourceCard
          kind="gsc"
          title="Google Search Console"
          category="Search demand & organic queries"
          sub="Ingests search queries, impressions, clicks, CTR and average position with a staged 16-month backfill."
          detected={detectedMap.get("gsc")}
          ok={gscOk}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {gscOauthOffered && <GoogleConnectButton slug={slug} kinds={["gsc"]} />}
            <GrantAccessInstead slug={slug} kinds={["gsc"]} onDone={() => onReload()} />
          </div>
        </StackSourceCard>

        <StackSourceCard
          kind="opinly"
          title="Opinly (AI & LLM Visibility)"
          category="AEO & Generative AI search"
          sub="Track your mention rate and brand citations across ChatGPT, Claude, and Gemini, plus competitive gap analysis."
          detected={detectedMap.get("opinly")}
          ok={opinlyOk}
        >
          <OpinlyConnectForm slug={slug} onDone={onReload} />
        </StackSourceCard>

        <StackSourceCard
          kind="bing"
          title="Bing Webmaster Tools"
          category="Microsoft search demand"
          sub="Bing organic queries, impressions, clicks, and page statistics alongside Google."
          ok={bingOk}
        >
          <BingConnectForm slug={slug} defaultUrl={domain ? (domain.startsWith("http") ? domain : `https://${domain}`) : ""} onDone={onReload} />
        </StackSourceCard>

        <StackSourceCard
          kind="ahrefs"
          title="Ahrefs"
          category="Domain authority & backlinks"
          sub="Weekly Domain Rating (DR), referring domains, and top keyword ranks without draining API units."
          ok={ahrefsOk}
        >
          <AhrefsConnectForm slug={slug} defaultDomain={domain.replace(/^https?:\/\//i, "").replace(/\/.*$/, "")} onDone={onReload} />
        </StackSourceCard>

        <StackSourceCard
          kind="semrush"
          title="SEMrush"
          category="Search visibility & rankings"
          sub="Weekly domain rank, organic keyword count, and search visibility trends."
          ok={semrushOk}
        >
          <SemrushConnectForm slug={slug} defaultDomain={domain.replace(/^https?:\/\//i, "").replace(/\/.*$/, "")} onDone={onReload} />
        </StackSourceCard>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button type="button" className="btn-solid" onClick={onNext}>
          {anyOk ? "Continue to Event Tracking →" : "Continue →"}
        </button>
        <button type="button" className="btn-ghost" onClick={onSkip}>
          Skip this stack
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// STAGE 4: Event Tracking
// ---------------------------------------------------------------------------
function TrackingStep({
  slug,
  domain,
  isConnected,
  onNext,
  onSkip,
}: {
  slug: string;
  domain: string;
  isConnected: (k: string) => boolean;
  onNext: () => void;
  onSkip: () => void;
}) {
  const gtmConnected = isConnected("gtm");

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1>4. Event tracking</h1>
          <span className="eyebrow" style={{ color: "var(--keeper)" }}>Step 4 of 5</span>
        </div>
        <span className="sub">
          Capture first-party funnel events (leads, signups, milestone conversions) and ad click identifiers directly from your site.
        </span>
      </div>

      <div className="onboarding-strategy-card" style={{ marginTop: 18 }}>
        <strong>How first-party event tracking works:</strong>
        <div>
          • <strong>1-Click GTM Deploy</strong>: {gtmConnected ? "Your Google Tag Manager container is connected! You can publish the FunnelKeeper tag into your live container with a single tap below." : "If you connect Tag Manager, you can auto-publish the FunnelKeeper tag without touching code."}
        </div>
        <div>
          • <strong>First-touch Attribution</strong>: Captures UTMs, referrers, and ad click parameters (<code>gclid</code>, <code>fbclid</code>, <code>li_fat_id</code>) before user signup.
        </div>
        <div>
          • <strong>Optionality</strong>: If your database or Stripe will be connected in Step 5, client-side tracking is optional but captures pre-registration visitor leads.
        </div>
      </div>

      {domain && (
        <div style={{ marginTop: 18 }}>
          <WebsiteDetector
            slug={slug}
            defaultUrl={domain}
            compact
            autoScan
            title="Tracking tag detector"
            subtitle="Verifies if the FunnelKeeper script or Google Tag Manager container is actively running on your site."
          />
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <TrackingInstall slug={slug} />
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button type="button" className="btn-solid" onClick={onNext}>
          Continue to Monetisation →
        </button>
        <button type="button" className="btn-ghost" onClick={onSkip}>
          Skip for now
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// STAGE 5: Bottom of Funnel & Monetisation
// ---------------------------------------------------------------------------
function MonetisationStep({
  slug,
  detectedMap,
  isConnected,
  onReload,
  onNext,
  onSkip,
}: {
  slug: string;
  detectedMap: Map<string, string>;
  isConnected: (k: string) => boolean;
  onReload: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const stripeOk = isConnected("stripe");
  const mysqlOk = isConnected("mysql");
  const postgresOk = isConnected("postgres");
  const mongoOk = isConnected("mongo");
  const anyOk = stripeOk || mysqlOk || postgresOk || mongoOk;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1>5. Bottom of funnel & monetisation</h1>
          <span className="eyebrow" style={{ color: anyOk ? "var(--green)" : "var(--text-faint)" }}>
            {anyOk ? "Connected" : "Step 5 of 5"}
          </span>
        </div>
        <span className="sub">
          Where customer revenue settles. Ingests payment charges, refunds, customer attribution, and database conversion records.
        </span>
      </div>

      <div className="onboarding-strategy-card" style={{ marginTop: 18 }}>
        <strong>How monetisation sources collaborate:</strong>
        <div>
          • <strong>Stripe</strong> automatically ingests succeeded charges as conversions and revenue in the native currency. Customer metadata (UTMs, click IDs) links ad spend to paying customers, and refunds or disputes net out cleanly.
        </div>
        <div>
          • <strong>Database Replicas (MySQL / Postgres / MongoDB)</strong> map user registrations, orders, and subscriptions to custom funnel stages and transactions using read-only roles.
        </div>
        <div>
          • <strong>Append-Only Facts</strong>: FunnelKeeper never modifies historical conversion records; corrections and refunds land as honest adjustments.
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
        <StackSourceCard
          kind="stripe"
          title="Stripe"
          category="Payment gateway & revenue ledger"
          sub="Succeeded charges become your bottom-of-funnel conversion and net revenue ledger. Refunds and disputes net out automatically."
          detected={detectedMap.get("stripe")}
          ok={stripeOk}
        >
          <StripeCredentialsForm slug={slug} onDone={onReload} />
        </StackSourceCard>

        <StackSourceCard
          kind="mysql"
          title="MySQL"
          category="Database replica to funnel events"
          sub="Turn rows from your MySQL database into mapped funnel events, identities, and transaction revenue."
          ok={mysqlOk}
        >
          <MysqlCredentialsForm slug={slug} onDone={onReload} />
        </StackSourceCard>

        <StackSourceCard
          kind="postgres"
          title="Postgres"
          category="Database replica to funnel events"
          sub="Turn rows from your Postgres tables into schema-aware customer journeys and conversion events."
          ok={postgresOk}
        >
          <PostgresCredentialsForm slug={slug} onDone={onReload} />
        </StackSourceCard>

        <StackSourceCard
          kind="mongo"
          title="MongoDB"
          category="Document collections to funnel events"
          sub="Turn documents from your MongoDB collections into funnel events and revenue transactions."
          ok={mongoOk}
        >
          <MongoCredentialsForm slug={slug} onDone={onReload} />
        </StackSourceCard>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button type="button" className="btn-solid" onClick={onNext}>
          {anyOk ? "Review Setup & Launch →" : "Continue →"}
        </button>
        <button type="button" className="btn-ghost" onClick={onSkip}>
          Skip this stack
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// STAGE 6: Review Scorecard & Launch
// ---------------------------------------------------------------------------
function DoneScorecard({
  slug,
  conns,
  added,
  onFinish,
}: {
  slug: string;
  conns: Connection[];
  added?: boolean;
  onFinish: () => void;
}) {
  const activeKinds = conns.filter((c) => c.product_slug === slug && c.status === "active").map((c) => c.kind);

  const getStackSources = (kinds: string[]) => {
    const matched = kinds.filter((k) => activeKinds.includes(k));
    return matched;
  };

  const analyticsSources = getStackSources(["ga4", "gtm", "posthog", "clarity"]);
  const socialSources = getStackSources(["meta_ads", "linkedin_ads", "google_ads", "meta_social", "x_social"]);
  const seoSources = getStackSources(["gsc", "bing", "opinly", "ahrefs", "semrush"]);
  const monetisationSources = getStackSources(["stripe", "mysql", "postgres", "mongo"]);

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <h1>{added ? "Your project is ready!" : "You're all set on the Growth trial"}</h1>
        <span className="sub" style={{ maxWidth: 560 }}>
          {added
            ? "Your connected sources are starting their initial 30-day backfill, deepening to 365 days and beyond in the background."
            : "30 days full access, no credit card required. Connected sources are now synchronising your historical funnel and spend."}
        </span>
      </div>

      <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Connected Stack Scorecard</div>
          <div className="onboarding-scorecard">
            <ScorecardTile title="1. Analytics" sources={analyticsSources} />
            <ScorecardTile title="2. Social & Ads" sources={socialSources} />
            <ScorecardTile title="3. SEO & AEO" sources={seoSources} />
            <ScorecardTile title="4. Tracking" sources={activeKinds.includes("gtm") ? ["gtm", "tracking"] : ["tracking"]} isTracking />
            <ScorecardTile title="5. Monetisation" sources={monetisationSources} />
          </div>
        </div>

        <div className="panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>What happens next:</div>
          <div style={{ fontSize: 12.5, color: "var(--text-3)", lineHeight: 1.6 }}>
            • <strong>Staged Backfill Ladder</strong>: Each source immediately backfills 30 days of data to populate your charts, then deepens to 365 days and full history.
            <br />
            • <strong>The Keeper Engine</strong>: Proposes actionable growth cards for conversion dropoffs, wasted spend, and high-impact optimizations.
            <br />
            • <strong>Integrations Hub</strong>: You can add or fine-tune event maps and credentials at any time in Integrations.
          </div>
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn-solid" style={{ minWidth: 200 }} onClick={onFinish}>
              Open Project Dashboard →
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function ScorecardTile({
  title,
  sources,
  isTracking,
}: {
  title: string;
  sources: string[];
  isTracking?: boolean;
}) {
  return (
    <div className="onboarding-scorecard-item">
      <span className="onboarding-scorecard-title">{title}</span>
      {sources.length > 0 ? (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 4 }}>
          {sources.map((k) => (
            <span
              key={k}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: "var(--surface-sunk)",
                padding: "3px 8px",
                borderRadius: 6,
                fontSize: 11.5,
                fontWeight: 500,
              }}
            >
              <IntegrationMark kind={k} size={14} />
              <span>{k.replace(/_/g, " ")}</span>
            </span>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 4 }}>
          {isTracking ? "Ready to track" : "Not connected"}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared Stack Source Card
// ---------------------------------------------------------------------------
function StackSourceCard({
  kind,
  title,
  category,
  sub,
  ok,
  detected,
  children,
}: {
  kind: string;
  title: string;
  category?: string;
  sub: string;
  ok: boolean;
  detected?: string | null;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="panel"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        borderColor: detected && !ok ? "var(--keeper)" : undefined,
        background: ok ? "color-mix(in srgb, var(--surface) 95%, var(--keeper))" : undefined,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ flex: "none" }}>
          <IntegrationMark kind={kind} size={28} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: 14.5 }}>{title}</span>
            {category && (
              <span style={{ fontSize: 11.5, color: "var(--text-faint)", fontWeight: 400 }}>
                · {category}
              </span>
            )}
            {detected && !ok && (
              <span className="eyebrow" style={{ color: "var(--keeper)", borderColor: "var(--keeper)", padding: "1px 6px" }}>
                Detected on site
              </span>
            )}
          </div>
          <div className="sub" style={{ marginTop: 2, fontSize: 12.5, lineHeight: 1.45 }}>
            {sub}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
          <span className="eyebrow" style={{ color: ok ? "var(--green)" : undefined }}>
            {ok ? "✓ connected" : "not connected"}
          </span>
          <button
            type="button"
            className={ok ? "btn-ghost" : "btn-solid"}
            style={{ fontSize: 12.5, padding: "6px 14px" }}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide" : ok ? "Configure" : "Connect"}
          </button>
        </div>
      </div>
      {open && (
        <div style={{ borderTop: "1px solid var(--border-hairline)", paddingTop: 12 }}>
          {children}
        </div>
      )}
    </div>
  );
}
